class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);
    this.freeShippingThreshold = 500;

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.notification.classList.add('animate', 'active');

    // Prevent body scroll when notification is open (mobile)
    if (window.innerWidth <= 749) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      this._scrollY = window.scrollY;
      document.body.style.top = `-${this._scrollY}px`;
    }

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    // Restore body scroll when notification is closed
    if (window.innerWidth <= 749) {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.top = '';
      if (this._scrollY !== undefined) {
        window.scrollTo(0, this._scrollY);
      }
    }

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState) {
    this.cartItemKey = parsedState.key;
    this.lastAddedProductId = parsedState.product_id;
    
    this.getSectionsToRender().forEach((section) => {
      document.getElementById(section.id).innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector
      );
    });

    // Fetch fresh cart data for progress bar (parsedState may not have total_price)
    this.fetchCartAndUpdateProgress();

    // Fetch upsell recommendations
    if (this.lastAddedProductId) {
      this.fetchUpsell(this.lastAddedProductId);
    }

    if (this.header) this.header.reveal();
    this.open();
  }

  fetchCartAndUpdateProgress() {
    fetch(`${window.Shopify.routes.root}cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        this.updateProgressBar(cart);
      })
      .catch((e) => {
        console.error('Error fetching cart for progress bar:', e);
      });
  }

  updateProgressBar(cartData) {
    const progressContainer = document.getElementById('cart-notification-progress');
    if (!progressContainer) return;

    // cart.total_price is in cents, convert to dollars
    const cartTotal = cartData.total_price ? cartData.total_price / 100 : 0;
    const amountRemaining = this.freeShippingThreshold - cartTotal;
    const progressPercent = Math.min((cartTotal / this.freeShippingThreshold) * 100, 100);

    // Update progress bar fill
    const progressFill = document.getElementById('cart-notification-progress-fill');
    const progressBar = progressContainer.querySelector('.cart-notification-progress__bar');
    if (progressFill) {
      progressFill.style.width = `${progressPercent}%`;
    }

    // Update progress text
    const progressText = progressContainer.querySelector('.cart-notification-progress__text');
    if (progressText) {
      if (amountRemaining > 0) {
        // Not qualified - show remaining amount (orange color)
        progressContainer.classList.remove('cart-notification-progress--complete');
        progressText.innerHTML = `
          <span class="cart-notification-progress__message">
            🔥 只差 <span class="cart-notification-progress__amount">$${Math.ceil(amountRemaining)}</span> 即享免運費！立即加購
          </span>
        `;
      } else {
        // Qualified - show complete message with green color (#22D06B)
        progressContainer.classList.add('cart-notification-progress--complete');
        progressText.innerHTML = `
          <span class="cart-notification-progress__message cart-notification-progress__message--complete">
            <svg class="cart-notification-progress__check-icon" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="7" cy="7" r="7" fill="#22D06B"/>
              <path d="M4 7L6 9L10 5" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            已享免運費！
          </span>
        `;
      }
    }
    
    // Always show progress bar
    if (progressBar) progressBar.style.display = 'block';

    // Update upsell container visibility
    const upsellContainer = document.getElementById('cart-notification-upsell');
    if (upsellContainer) {
      if (amountRemaining <= 0) {
        // Qualified for free shipping - hide upsell
        upsellContainer.dataset.showUpsell = 'false';
        upsellContainer.innerHTML = '';
        upsellContainer.style.display = 'none';
      } else {
        // Not qualified - show upsell
        upsellContainer.dataset.showUpsell = 'true';
        upsellContainer.style.display = 'block';
      }
    }
  }

  fetchUpsell(productId) {
    const upsellContainer = document.getElementById('cart-notification-upsell');
    if (!upsellContainer || upsellContainer.dataset.showUpsell === 'false') return;

    const url = `${window.Shopify.routes.root}recommendations/products?product_id=${productId}&limit=100&intent=complementary&section_id=cart-upsell`;

    fetch(url)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        
        const recommendations = html.querySelector('.cart-upsell');
        if (recommendations) {
          upsellContainer.innerHTML = recommendations.outerHTML;
          this.initUpsellEventListeners();
        }
      })
      .catch((e) => {
        console.error('Error fetching upsell recommendations:', e);
      });
  }

  initUpsellEventListeners() {
    const upsellContainer = document.getElementById('cart-notification-upsell');
    if (!upsellContainer) return;

    // Quantity buttons
    upsellContainer.querySelectorAll('.cart-upsell-item__qty-btn').forEach((btn) => {
      btn.addEventListener('click', this.handleQuantityChange.bind(this));
    });

    // Add to cart buttons
    upsellContainer.querySelectorAll('.cart-upsell-item__add-btn').forEach((btn) => {
      btn.addEventListener('click', this.handleUpsellAddToCart.bind(this));
    });

    // Initialize custom scrollbar
    this.initCustomScrollbar(upsellContainer);
  }

  initCustomScrollbar(container) {
    const productsList = container.querySelector('.cart-upsell__products');
    const scrollTrack = container.querySelector('.cart-upsell__scrollbar-track');
    const scrollThumb = container.querySelector('.cart-upsell__scrollbar-thumb');

    if (!productsList || !scrollTrack || !scrollThumb) return;

    const updateScrollbar = () => {
      const scrollHeight = productsList.scrollHeight;
      const clientHeight = productsList.clientHeight;
      const scrollTop = productsList.scrollTop;

      // Check if content overflows
      if (scrollHeight <= clientHeight || clientHeight === 0) {
        scrollTrack.classList.add('hidden');
        scrollThumb.classList.add('hidden');
        return;
      }

      scrollTrack.classList.remove('hidden');
      scrollThumb.classList.remove('hidden');

      // Calculate thumb height (proportional to visible area, min 30px)
      const thumbHeightPercent = (clientHeight / scrollHeight) * 100;
      const thumbHeight = Math.max(thumbHeightPercent, 20);
      scrollThumb.style.height = `${thumbHeight}%`;

      // Calculate thumb position as percentage
      const maxScrollTop = scrollHeight - clientHeight;
      const scrollPercent = maxScrollTop > 0 ? (scrollTop / maxScrollTop) : 0;
      const maxThumbTop = 100 - thumbHeight;
      const thumbTop = scrollPercent * maxThumbTop;
      scrollThumb.style.top = `${thumbTop}%`;
    };

    // Initial update with delays to ensure rendering
    const initWithDelay = () => {
      updateScrollbar();
      setTimeout(updateScrollbar, 50);
      setTimeout(updateScrollbar, 150);
      setTimeout(updateScrollbar, 500);
    };

    initWithDelay();

    // Update on scroll
    productsList.addEventListener('scroll', updateScrollbar, { passive: true });

    // Update on resize
    window.addEventListener('resize', updateScrollbar, { passive: true });
  }

  handleQuantityChange(event) {
    const button = event.currentTarget;
    const item = button.closest('.cart-upsell-item');
    const qtyDisplay = item.querySelector('.cart-upsell-item__qty-value');
    let qty = parseInt(qtyDisplay.textContent) || 1;

    if (button.name === 'plus') {
      qty += 1;
    } else if (button.name === 'minus' && qty > 1) {
      qty -= 1;
    }

    qtyDisplay.textContent = qty;
  }

  handleUpsellAddToCart(event) {
    const button = event.currentTarget;
    const item = button.closest('.cart-upsell-item');
    const variantId = button.dataset.variantId;
    const productId = item.dataset.productId;
    const qtyDisplay = item.querySelector('.cart-upsell-item__qty-value');
    const quantity = parseInt(qtyDisplay.textContent) || 1;

    if (!variantId) return;

    // Show full loading overlay
    this.showLoading();

    const formData = {
      items: [
        {
          id: parseInt(variantId),
          quantity: quantity,
        },
      ],
    };

    fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          console.error('Error adding to cart:', response.description);
          this.hideLoading();
          return;
        }

        // Store the newly added product ID for refreshing
        this.lastAddedProductId = productId;
        
        // Fetch updated cart and refresh the entire notification
        this.refreshCartNotificationFull(response);
      })
      .catch((e) => {
        console.error('Error adding to cart:', e);
        this.hideLoading();
      });
  }

  showLoading() {
    const loadingOverlay = document.getElementById('cart-notification-loading');
    if (loadingOverlay) {
      loadingOverlay.classList.add('active');
      loadingOverlay.setAttribute('aria-hidden', 'false');
    }
  }

  hideLoading() {
    const loadingOverlay = document.getElementById('cart-notification-loading');
    if (loadingOverlay) {
      loadingOverlay.classList.remove('active');
      loadingOverlay.setAttribute('aria-hidden', 'true');
    }
  }

  refreshCartNotificationFull(addedItemResponse) {
    // Get cart data first
    fetch(`${window.Shopify.routes.root}cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        // Update the product display to show the newly added item
        const productContainer = document.getElementById('cart-notification-product');
        if (productContainer && addedItemResponse.items && addedItemResponse.items.length > 0) {
          const item = addedItemResponse.items[0];
          const formattedPrice = '$' + (item.price / 100).toFixed(0);
          
          productContainer.innerHTML = `
            <div class="cart-notification-product__image global-media-settings">
              ${item.image ? `<img src="${item.image}" alt="${item.title}" width="70" loading="lazy">` : ''}
            </div>
            <div class="cart-notification-product__details">
              <h3 class="cart-notification-product__name h4">${item.product_title}</h3>
              <div class="cart-notification-product__meta">
                ${item.variant_title ? `<p class="cart-notification-product__option">Package: ${item.variant_title}</p>` : ''}
                <p class="cart-notification-product__price">${formattedPrice}</p>
              </div>
            </div>
          `;
        }

        // Update cart icon bubble in header
        const bubble = document.getElementById('cart-icon-bubble');
        if (bubble) {
          let countBubble = bubble.querySelector('.cart-count-bubble');
          
          if (countBubble) {
            // Badge exists, update the count
            const countEl = countBubble.querySelector('span[aria-hidden="true"]');
            if (countEl) countEl.textContent = cart.item_count;
          } else if (cart.item_count > 0) {
            // Badge doesn't exist - fetch updated header section
            fetch('/?sections=cart-icon-bubble')
              .then(res => res.json())
              .then(sections => {
                if (sections['cart-icon-bubble']) {
                  const tempDiv = document.createElement('div');
                  tempDiv.innerHTML = sections['cart-icon-bubble'];
                  const newBubble = tempDiv.querySelector('#cart-icon-bubble');
                  if (newBubble) {
                    bubble.innerHTML = newBubble.innerHTML;
                  }
                }
              });
          }
        }

        // Update "View Cart" button count - update all matching elements
        document.querySelectorAll('.cart-notification-button__count').forEach(el => {
          el.textContent = cart.item_count;
        });

        // Update progress bar
        this.updateProgressBar(cart);

        // Store the new product ID and fetch upsell recommendations
        if (addedItemResponse.items && addedItemResponse.items.length > 0) {
          this.lastAddedProductId = addedItemResponse.items[0].product_id;
          this.fetchUpsell(this.lastAddedProductId);
        }

        // Hide loading overlay
        this.hideLoading();
      })
      .catch((e) => {
        console.error('Error refreshing cart notification:', e);
        this.hideLoading();
      });
  }

  refreshCartNotification() {
    fetch(`${window.Shopify.routes.root}cart.js`)
      .then((response) => response.json())
      .then((cart) => {
        // Update progress bar
        this.updateProgressBar(cart);

        // Update "View Cart" button count - update all matching elements
        document.querySelectorAll('.cart-notification-button__count').forEach(el => {
          el.textContent = cart.item_count;
        });

        // Update cart icon bubble
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          fetch(`${window.location.pathname}?sections=cart-icon-bubble`)
            .then((response) => response.json())
            .then((sections) => {
              if (sections['cart-icon-bubble']) {
                const html = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
                const newBubble = html.querySelector('#cart-icon-bubble');
                if (newBubble) {
                  cartIconBubble.innerHTML = newBubble.innerHTML;
                }
              }
            });
        }

        // Re-fetch upsell if still eligible
        if (this.lastAddedProductId) {
          this.fetchUpsell(this.lastAddedProductId);
        }
      })
      .catch((e) => {
        console.error('Error refreshing cart:', e);
      });
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
